import { BULK_SERVICES_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/services/BulkServicesAssignmentTable';
import {
    BulkVisitRequestWorkflowUpdateAppointmentServicesMutation,
    BulkVisitRequestWorkflowUpdateAppointmentServicesMutationVariables,
} from '@/components/visitRequests/mutations/updateAppointmentServices.gql.types';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { errorToast } from '@/toast';
import { gql, useMutation } from '@apollo/client';
import type { MutationHookOptions } from '@apollo/client/react/types/types';
import {
    BulkUpdateClinicianAppointmentServicesInput,
    UpdateVisitRequestClinicianAppointmentServicesInput,
} from 'gql-types';

import { UNKNOWN_CLINICIAN_APPOINTMENT_ID } from './constants';

// Individual Visit Request Appointment Services Update Mutation
export const useUpdateAppointmentServices = ({
    visitRequestId,
    clinicianAppointmentId,
}: {
    visitRequestId: string;
    // Clinician appointment ID is optional because the visit request may not have any appointments yet.
    // TODO: Remove this once we ensure that a visit request always has at least one appointment created with it.
    clinicianAppointmentId: string | null;
}) => {
    // Assign clinician mutation.
    const [mutate, result] = useMutation(UPDATE_APPOINTMENT_SERVICES_MUTATION, {
        variables: {
            visitRequestId,
            clinicianAppointmentId:
                clinicianAppointmentId === UNKNOWN_CLINICIAN_APPOINTMENT_ID
                    ? null
                    : clinicianAppointmentId,
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_SERVICES_ASSIGNMENT_TABLE_QUERY],
        onError: (error) => {
            if (error instanceof Error) {
                errorToast(error.message);
            }
        },
    });

    function updateServices({ serviceIds }: { serviceIds: string[] }) {
        return mutate({
            variables: {
                serviceIds,
            },
        });
    }

    return { updateServices, ...result };
};

export interface IUpdateAppointmentServicesInput {
    visitRequestId: string;
    clinicianAppointmentId: string;
    serviceIds: string[];
}

type UpdateAppointmentServicesParams = {
    assignments: UpdateVisitRequestClinicianAppointmentServicesInput[];
    options?: Pick<MutationHookOptions, 'onError' | 'onCompleted'>;
};

// Bulk Visit Request Appointment Services Assignment Mutation
export const useBulkUpdateAppointmentServices = () => {
    const [mutate, results] = useMutation<
        BulkVisitRequestWorkflowUpdateAppointmentServicesMutation,
        BulkVisitRequestWorkflowUpdateAppointmentServicesMutationVariables
    >(BULK_UPDATE_APPOINTMENT_SERVICES_MUTATION, {
        onError: (error) => {
            if (error instanceof Error) {
                errorToast(error.message);
            }
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_SERVICES_ASSIGNMENT_TABLE_QUERY],
    });

    const updateAppointmentServices = (params: UpdateAppointmentServicesParams) => {
        const { assignments, options } = params;
        return mutate({
            variables: {
                input: {
                    visitRequests: assignments,
                },
            },
            refetchQueries: [ALL_VISIT_COUNTS, BULK_SERVICES_ASSIGNMENT_TABLE_QUERY],
            ...options,
        });
    };

    return { updateAppointmentServices, ...results };
};

type UpdateVisitAppointmentServicesParams = {
    input: BulkUpdateClinicianAppointmentServicesInput;
    options?: Pick<MutationHookOptions, 'onError' | 'onCompleted'>;
};

// Bulk Visit Appointment Services Assignment Mutation
export const useBulkUpdateVisitAppointmentServices = () => {
    const [mutate, results] = useMutation(BULK_UPDATE_VISIT_APPOINTMENT_SERVICES_MUTATION, {
        onError: (error) => {
            if (error instanceof Error) {
                errorToast(error.message);
            }
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_SERVICES_ASSIGNMENT_TABLE_QUERY],
    });

    const updateVisitAppointmentServices = (params: UpdateVisitAppointmentServicesParams) => {
        const { input, options } = params;

        return mutate({
            variables: { input },
            refetchQueries: [ALL_VISIT_COUNTS, BULK_SERVICES_ASSIGNMENT_TABLE_QUERY],
            ...options,
        });
    };

    return { updateVisitAppointmentServices, ...results };
};

// Returning the updated Visit (state, outcome, workflowStep) alongside the appointment's
// services lets Apollo's normalized cache merge the new values into rows automatically —
// no refetch needed. Mirrors the parity established for clinician (ENG-8299) and date (ENG-8300).
const ClinicianAppointmentServicesFragment = gql`
    fragment ClinicianAppointmentServicesFragment on ClinicianAppointment {
        id
        services {
            id
            name
        }
        durationMinutes
        durationOverrideMinutes
        visit {
            id
            state
            outcome
            workflowStep
            priority
            effectivePriority
            durationMinutes
            durationOverrideMinutes
        }
    }
`;

const BULK_UPDATE_VISIT_APPOINTMENT_SERVICES_MUTATION = gql`
    mutation BulkUpdateVisitAppointmentServices(
        $input: BulkUpdateClinicianAppointmentServicesInput!
    ) {
        bulkUpdateClinicianAppointmentServices(input: $input) {
            results {
                resource {
                    ...ClinicianAppointmentServicesFragment
                }
                errors {
                    message
                }
                success
            }
        }
    }
    ${ClinicianAppointmentServicesFragment}
`;

const VisitRequestServicesAssignmentFragment = gql`
    fragment VisitRequestServicesAssignmentFragment on VisitRequest {
        id
        state
        workflowStep
        priority
        appointments {
            id
            services {
                id
                name
            }
        }
    }
`;
const BULK_UPDATE_APPOINTMENT_SERVICES_MUTATION = gql`
    mutation BulkVisitRequestWorkflowUpdateAppointmentServices(
        $input: BulkUpdateVisitRequestClinicianAppointmentServicesInput!
    ) {
        bulkUpdateVisitRequestClinicianAppointmentServices(input: $input) {
            results {
                resource {
                    ...VisitRequestServicesAssignmentFragment
                }
                errors {
                    message
                }
                success
            }
        }
    }
    ${VisitRequestServicesAssignmentFragment}
`;

const UPDATE_APPOINTMENT_SERVICES_MUTATION = gql`
    mutation UpdateVisitRequestAppointmentServices(
        $visitRequestId: UUID!
        $clinicianAppointmentId: UUID
        $serviceIds: [UUID!]!
    ) {
        updateVisitRequestClinicianAppointmentServices(
            input: {
                visitRequestId: $visitRequestId
                clinicianAppointmentId: $clinicianAppointmentId
                serviceIds: $serviceIds
            }
        ) {
            ...VisitRequestServicesAssignmentFragment
        }
    }
    ${VisitRequestServicesAssignmentFragment}
`;
